import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, CalendarDays, List } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import CalendarView from "@/components/CalendarView";
import ConfirmTypeDialog from "@/components/ConfirmTypeDialog";

const DEFAULT_CHECKLIST = (category) => {
  const generic = ["Visual condition", "Safety labels & markings", "Documentation review"];
  if (category === "vehicle") return [...generic, "Tyres & braking system", "Lights & signaling", "Fluid levels", "Emergency equipment"];
  if (category === "industrial_equipment") return [...generic, "Structural integrity", "Guards & interlocks", "Emergency stops", "Lubrication & wear", "Electrical safety"];
  return generic;
};

export default function Inspections() {
  const { user } = useAuth();
  const canCreate = ["admin","super_admin"].includes(user?.role);
  const canDelete = ["admin","super_admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ asset_id: "", inspector_id: "", scheduled_date: new Date().toISOString().slice(0, 10), inspection_type: "routine", notes: "" });
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("list");
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    const [i, a, u] = await Promise.all([api.get("/inspections"), api.get("/assets"), api.get("/users")]);
    setItems(i.data); setAssets(a.data); setUsers(u.data);
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.asset_id) return toast.error("Select an asset");
    const asset = assets.find((a) => a.id === form.asset_id);
    const checklist = DEFAULT_CHECKLIST(asset?.category).map((label) => ({ label, result: null, notes: "" }));
    setSaving(true);
    try {
      await api.post("/inspections", { ...form, inspector_id: form.inspector_id || null, checklist });
      toast.success("Inspection scheduled");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/inspections/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="inspections-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Schedule</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Inspections</h1>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-inspection-button" className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]"><Plus className="w-4 h-4 mr-2" /> Schedule Inspection</Button>
            </DialogTrigger>
            <DialogContent className="rounded-md">
              <DialogHeader><DialogTitle className="font-display">Schedule Inspection</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Asset *</Label>
                  <Select value={form.asset_id} onValueChange={(v) => setForm({ ...form, asset_id: v })}>
                    <SelectTrigger className="rounded-md mt-1" data-testid="inspection-asset-select"><SelectValue placeholder="Select asset" /></SelectTrigger>
                    <SelectContent>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.asset_tag})</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Assigned Inspector</Label>
                  <Select value={form.inspector_id} onValueChange={(v) => setForm({ ...form, inspector_id: v })}>
                    <SelectTrigger className="rounded-md mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>{users.filter(u => u.role === 'inspector' || u.role === 'admin').map((u) => <SelectItem key={u.id} value={u.id}>{u.name} — {u.role}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">Scheduled Date *</Label><Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="rounded-md mt-1" /></div>
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">Type</Label>
                    <Select value={form.inspection_type} onValueChange={(v) => setForm({ ...form, inspection_type: v })}>
                      <SelectTrigger className="rounded-md mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="routine">Routine</SelectItem><SelectItem value="annual">Annual</SelectItem><SelectItem value="pre_use">Pre-use</SelectItem><SelectItem value="post_incident">Post-incident</SelectItem></SelectContent>
                    </Select></div>
                </div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-md mt-1" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="save-inspection-button" disabled={saving} onClick={submit} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">{saving ? "Saving..." : "Schedule"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-1 mb-4" data-testid="view-toggle">
        <button
          data-testid="view-list-btn"
          onClick={() => setView("list")}
          className={`inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider font-semibold border transition-colors ${
            view === "list" ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
          }`}
        >
          <List className="w-3.5 h-3.5" /> List
        </button>
        <button
          data-testid="view-calendar-btn"
          onClick={() => setView("calendar")}
          className={`inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider font-semibold border transition-colors ${
            view === "calendar" ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
          }`}
        >
          <CalendarDays className="w-3.5 h-3.5" /> Calendar
        </button>
      </div>

      {view === "calendar" ? (
        <CalendarView inspections={items} />
      ) : (
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <Th>Date</Th><Th>Asset</Th><Th>Type</Th><Th>Inspector</Th><Th>Status</Th><Th>Result</Th><Th></Th>
            </tr>
          </thead>
          <tbody data-testid="inspections-table">
            {items.length === 0 ? <tr><td colSpan={7} className="text-center py-10 text-gray-500">No inspections scheduled yet.</td></tr>
              : items.map((i) => (
                <tr key={i.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`inspection-row-${i.id}`}>
                  <Td className="font-mono">{i.scheduled_date?.slice(0,10)}</Td>
                  <Td><Link to={`/inspections/${i.id}`} className="font-semibold text-[#01ADEF] hover:underline">{i.asset_name || '—'}</Link><div className="text-xs text-gray-500 font-mono">{i.asset_tag}</div></Td>
                  <Td className="capitalize">{i.inspection_type?.replace('_',' ')}</Td>
                  <Td>{i.inspector_name || <span className="text-gray-400">Unassigned</span>}</Td>
                  <Td><StatusPill v={i.status} /></Td>
                  <Td>{i.result ? <ResultPill v={i.result} /> : <span className="text-gray-400 text-xs">—</span>}</Td>
                  <Td className="flex items-center gap-3">
                    <Link to={`/inspections/${i.id}`} className="text-xs uppercase tracking-wider font-semibold text-[#01ADEF]" data-testid={`open-inspection-${i.id}`}>Open</Link>
                    {user?.role === 'admin' && <button data-testid={`delete-inspection-${i.id}`} onClick={() => setConfirmDel(i)} className="text-gray-400 hover:text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>}
                  </Td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      )}
      <ConfirmTypeDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        phrase="DELETE"
        title="Delete inspection?"
        description={`This permanently removes the inspection${confirmDel?.asset_name ? ` for ${confirmDel.asset_name}` : ''}. Any related certificates will be orphaned but not deleted.`}
        confirmLabel="Delete Inspection"
        onConfirm={async () => { await remove(confirmDel.id); }}
        testidPrefix="inspection-delete"
      />
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const StatusPill = ({ v }) => {
  const map = { scheduled: ["bg-[#E0F2FE]","text-[#075985]","border-[#7DD3FC]"], in_progress: ["bg-[#FEF3C7]","text-[#92400E]","border-[#FCD34D]"], completed: ["bg-[#D1FAE5]","text-[#065F46]","border-[#6EE7B7]"] };
  const [bg,tx,br] = map[v] || map.scheduled;
  return <span className={`inline-block text-xs font-bold px-2 py-1 uppercase tracking-wider border ${bg} ${tx} ${br}`}>{v?.replace('_',' ')}</span>;
};
const ResultPill = ({ v }) => v === 'pass'
  ? <span className="inline-block text-xs font-bold px-2 py-1 uppercase tracking-wider border bg-[#D1FAE5] text-[#065F46] border-[#6EE7B7]">Pass</span>
  : <span className="inline-block text-xs font-bold px-2 py-1 uppercase tracking-wider border bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]">Fail</span>;
